import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import Helmet from 'common/helmets/Helmet';
import PallyyLandingHelmet from 'common/helmets/PallyyLandingHelmet';
import Button from 'common/inputs/Button';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import { P } from 'common/ui/Text';
import UppercaseHeader from 'common/UppercaseHeader';
import PallyyImage from 'img/landing/pallyy-image.webp';
import PallyyWordmark from 'img/landing/pallyy-wordmark.webp';
import Tim from 'img/landing/tim-bennetto.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const PallyyLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Pallyy Case Study');
  }, [trackEvent]);

  return (
    <div className="landingPallyy landingCustomersPage">
      <Helmet>
        <script async src="https://platform.twitter.com/widgets.js" />
      </Helmet>
      <PallyyLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <div className="text">
            <UppercaseHeader>Customer story</UppercaseHeader>
            <h1>How Canny helps Pallyy save time and shape their&nbsp;roadmap</h1>
            <Link className="cta" to="/register">
              <Button buttonType="greenButton" value="Get Started" />
            </Link>
          </div>
          <div className="headerImage">
            <div className="nametag">
              <P fontWeight="bold">Tim Bennetto</P>
              <P className="title">Founder of Pallyy</P>
            </div>
            <LazyLoadedImage loading="eager" className="image" src={Tim} alt="Tim Bennetto" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Tim Bennetto is a full-stack developer and the founder at Pallyy. For the last four
              years, Tim built Pallyy from the ground up. His main goal is to build an affordable
              feature-rich social media management and scheduling&nbsp;platform.
            </p>
            <p>
              Since Pallyy’s inception, Tim has been handling every aspect of the business.
              That&nbsp;includes:
            </p>
            <ul>
              <li>Building the platform</li>
              <li>Supporting customers</li>
              <li>Handling feature requests</li>
              <li>Promoting on social media</li>
              <li>And much more</li>
            </ul>
            <p>
              With 1.4k paying customers and 60k free users, Pallyy is still a one-person show. It’s
              easy to put feedback management on the back burner. But that’s the opposite of what
              Tim&nbsp;did.
            </p>
            <h2 className="sectionTitle">Problem</h2>
            <p>
              It’s one thing to <span className="innerBold">want</span> to collect feedback. It’s
              another thing to actually get&nbsp;it.
            </p>
            <p className="quote">
              &ldquo;The problem I’ve faced as a solo founder is getting feedback from users. In the
              beginning, I would send emails asking for feedback. However, the response rate was
              very low. It also took a lot of time that I didn’t&nbsp;have.&rdquo;
            </p>
            <p>
              Email outreach can be effective, especially when you’re just starting out. But then
              you need to combine all the feedback you receive and try to find similarities. You
              also need to understand what each piece of feedback means and how vital it is for
              your&nbsp;business.
            </p>
            <p>
              Also, timing matters. Your users may receive a survey request when they’re happy with
              the platform. So, feedback will be all positive. Later, they may encounter a problem,
              but they won’t have a way to share their feedback anymore. As a result, you won’t have
              an accurate representation of your customer&nbsp;sentiment.
            </p>
            <p className="quote">
              &ldquo;When growing a SaaS platform, you need to get feedback at the right time. This
              is where Canny came in&nbsp;handy.&rdquo;
            </p>
            <blockquote className="twitter-tweet">
              <p lang="en" dir="ltr">
                Startup founders, how do you collect user feedback from your SaaS users? I&#39;m
                currently using this welcome email for the new sign-ups.The goal is to understand
                their use case, but I could also ask which feature to add. Good 👍 Bad 👎 ?{' '}
                <a href="https://t.co/647l3d8QTb">pic.twitter.com/647l3d8QTb</a>
              </p>
              &mdash; Luca Restagno 🐢 userdesk.io (@ikoichi){' '}
              <a href="https://twitter.com/ikoichi/status/1701566375926657058?ref_src=twsrc%5Etfw">
                September 12, 2023
              </a>
            </blockquote>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Tim is dedicated to building the best platform out there. So, listening to his users
              was a top&nbsp;priority.
            </p>
            <p>
              Tim started with two separate boards: one for feature requests and the other one for
              bugs. He added links to those boards inside users’ Pallyy dashboards. Those links are
              always visible. This way, whenever a user notices a bug or has a suggestion, they can
              immediately submit their&nbsp;feedback.
            </p>
            <p className="quote">
              &ldquo;The feedback started coming in on auto-pilot. No more sending cold emails – it
              was just a stream of incoming tickets. This helped guide Pallyy in the early days and
              get us to where we are&nbsp;today.&rdquo;
            </p>
            <p>Once Tim prioritizes this feedback, he adds it to the public&nbsp;roadmap.</p>
            <blockquote className="twitter-tweet">
              <p lang="en" dir="ltr">
                The easiest way to get feedback for a SaaS is through a public roadmap.{' '}
                <a href="https://t.co/oSbPa49IkD">pic.twitter.com/oSbPa49IkD</a>
              </p>
              &mdash; Tim Bennetto (@Timb03){' '}
              <a href="https://twitter.com/Timb03/status/1678909866415644673?ref_src=twsrc%5Etfw">
                July 11, 2023
              </a>
            </blockquote>
            <p>
              This way, he’s transparent about what he’s building, and users get to see
              Pallyy’s&nbsp;journey.
            </p>
            <p>
              Tim didn’t stop there. He also uses Canny’s changelog to announce updates. Pallyy’s
              dashboard features a link to its changelog as well. Tim believes that their customers
              feel like they’re in the loop thanks to the changelog. It also reaffirms their
              decision to choose&nbsp;Pallyy.
            </p>
            <LazyLoadedImage src={PallyyImage} alt="Pallyy Product" />
            <h2 className="sectionTitle">Results</h2>
            <p>
              One of the hottest commodities for solopreneurs is time. There’s never enough time to
              do everything. That’s why Tim values Canny so&nbsp;much.
            </p>
            <p className="quote">
              &ldquo;It’s saved countless hours of sending emails and guided Pallyy into becoming
              one of the most popular social media management platforms around&nbsp;today.&rdquo;
            </p>
            <p>
              In fact, Tim mentioned that handling so many feature requests would be impossible
              without&nbsp;Canny.
            </p>
            <p className="quote">
              &ldquo;Canny has been invaluable. In fact, we’re still using the
              free&nbsp;plan.&rdquo;
            </p>
            <p>
              We’re on a mission to empower startups just like Pallyy. We believe that feedback
              management can really make a difference. And Tim&nbsp;agrees.
            </p>
            <p className="quote">
              &ldquo;If you’re starting a SaaS, adding Canny is one of the most cost-effective and
              high ROI things you can&nbsp;do.&rdquo;
            </p>
            <p>
              Are you also building a startup? Simplify your feedback management with Canny! Check
              out our discounts for startups, or get started with a free plan.{' '}
              <a target="_blank" href="https://canny.io/request-demo" rel="noopener">
                Book a demo here.
              </a>
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              className="PallyyWordmark"
              src={PallyyWordmark}
              alt="Pallyy Wordmark"
            />
            Pallyy is a social media management platform for brands and agencies. It helps with
            creating and scheduling posts to all of your social platforms, replying to comments and
            DMs, and checking your analytics to see what&nbsp;works.
            <a href="https://pallyy.com/" rel="noopener" target="_blank">
              pallyy.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>2</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2019</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Years on Canny</UppercaseHeader>
              <p>2</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>1,942</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded />
      </div>
    </div>
  );
};

export default PallyyLanding;
